import { isReferrerHost, isWellFormedChannel, unknownChannel } from './telemetry-identity';

export type ShopConfig = {
    enabled: boolean;
    unitPriceCents: number;
    currency: string;
    shipWithinBusinessDays: number;
    returnWindowBusinessDays: number;
};

export type CheckoutStatus = {
    status: 'paid' | 'processing' | 'unpaid';
    orderNumber: string | null;
    items: { title: string; quantity: number }[];
    shipWithinBusinessDays: number;
};

export const disabledShopConfig: ShopConfig = Object.freeze({
    enabled: false,
    unitPriceCents: 0,
    currency: 'usd',
    shipWithinBusinessDays: 3,
    returnWindowBusinessDays: 3
});

function readDays(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 60 ? value : fallback;
}

/**
 * Reads the optional shop block from /api/site-config. Anything missing or malformed disables ordering rather than
 * breaking the gallery.
 */
export function normalizeShopConfig(payload: unknown): ShopConfig {
    if (payload == null || typeof payload !== 'object') return disabledShopConfig;
    const candidate = payload as Record<string, unknown>;
    if (candidate.enabled !== true
        || typeof candidate.unitPriceCents !== 'number'
        || !Number.isInteger(candidate.unitPriceCents)
        || candidate.unitPriceCents <= 0
        || typeof candidate.currency !== 'string'
        || !/^[a-z]{3}$/.test(candidate.currency)) {
        return disabledShopConfig;
    }

    return {
        enabled: true,
        unitPriceCents: candidate.unitPriceCents,
        currency: candidate.currency,
        shipWithinBusinessDays: readDays(candidate.shipWithinBusinessDays, disabledShopConfig.shipWithinBusinessDays),
        returnWindowBusinessDays: readDays(candidate.returnWindowBusinessDays, disabledShopConfig.returnWindowBusinessDays)
    };
}

/** Formats a price for display, dropping cents when the amount is whole (e.g. "$59"). */
export function formatPrintPrice(unitPriceCents: number, currency: string, locale = 'en-US'): string {
    const amount = unitPriceCents / 100;
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
    }).format(amount);
}

/** Replaces {name} placeholders in a site string. */
export function formatSiteString(template: string, values: Record<string, string | number>): string {
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
        Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match);
}

export type CheckoutRequestBody = { titleSlug: string; quantity: number; channel: string; referrerHost?: string };

/**
 * The body of a checkout request: the photograph, one print, and the visitor session's channel so the order can say
 * where its buyer came from, plus, for the other channel only, the referring site's host. No browser or session ID
 * is sent; a malformed channel is sent as unknown, and a malformed host is left out.
 */
export function createCheckoutRequestBody(titleSlug: string, channel: string, referrerHost: string | null = null): CheckoutRequestBody {
    const body: CheckoutRequestBody = { titleSlug, quantity: 1, channel: isWellFormedChannel(channel) ? channel : unknownChannel };
    if (body.channel === 'other' && isReferrerHost(referrerHost)) body.referrerHost = referrerHost;
    return body;
}

/** Extracts the Stripe Checkout Session ID that Stripe appends to the thank-you URL. */
export function getCheckoutSessionIdFromSearch(search: string): string | null {
    const sessionId = new URLSearchParams(search).get('session_id');
    return sessionId != null && /^cs_(test|live)_[A-Za-z0-9]{10,200}$/.test(sessionId) ? sessionId : null;
}

export function normalizeCheckoutStatus(payload: unknown): CheckoutStatus | null {
    if (payload == null || typeof payload !== 'object') return null;
    const candidate = payload as Record<string, unknown>;
    if (candidate.status !== 'paid' && candidate.status !== 'processing' && candidate.status !== 'unpaid') return null;
    if (candidate.orderNumber != null && typeof candidate.orderNumber !== 'string') return null;
    if (!Array.isArray(candidate.items)) return null;
    const items: { title: string; quantity: number }[] = [];
    for (const item of candidate.items) {
        if (item == null || typeof item !== 'object') return null;
        const { title, quantity } = item as Record<string, unknown>;
        if (typeof title !== 'string' || typeof quantity !== 'number' || !Number.isInteger(quantity) || quantity < 0) return null;
        items.push({ title, quantity });
    }

    const shipWithinBusinessDays = typeof candidate.shipWithinBusinessDays === 'number'
        && Number.isInteger(candidate.shipWithinBusinessDays)
        && candidate.shipWithinBusinessDays > 0
        ? candidate.shipWithinBusinessDays
        : 3;
    return {
        status: candidate.status,
        orderNumber: typeof candidate.orderNumber === 'string' ? candidate.orderNumber : null,
        items,
        shipWithinBusinessDays
    };
}
